import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarComponent, ReferralStatisticsChart } from '../components/Placeholder'
import ReferralsTab from '../components/ReferralsTab';
import ConnectionsTab from '../components/ConnectionTab';
import { 
  FaUser, FaBriefcase, FaCalendarAlt, FaNetworkWired, FaGraduationCap, 
  FaChartLine, FaBell, FaEnvelope, FaCog, FaSearch, FaPlus,
  FaMapMarkerAlt, FaPhone, FaLinkedin, FaIndustry, FaGlobe, FaHandHoldingHeart,
  FaTwitter, FaGithub, FaInstagram, FaFileAlt, FaUniversity, FaUserFriends,
  FaDollarSign, FaBook, FaTrophy, FaComments, FaNewspaper, FaChartBar,
  FaUserGraduate, FaProjectDiagram, FaLightbulb, FaCode, FaTimes,FaChartPie
} from 'react-icons/fa';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Dummy data for charts
const networkGrowthData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  datasets: [
    {
      label: 'Network Growth',
      data: [50, 75, 100, 150, 200, 250],
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
      fill: true,
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
    },
  ],
};

const skillsData = {
  labels: ['JavaScript', 'React', 'Node.js', 'Python', 'SQL', 'AWS'],
  datasets: [
    {
      label: 'Skill Level',
      data: [9, 8, 7, 8, 6, 7],
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      borderColor: 'rgb(255, 99, 132)',
      pointBackgroundColor: 'rgb(255, 99, 132)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgb(255, 99, 132)'
    }
  ]
};

const donationData = {
  labels: ['Scholarship Fund', 'Research Grants', 'Campus Improvement', 'Student Activities'],
  datasets: [
    {
      data: [30000, 25000, 20000, 15000],
      backgroundColor: [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 206, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
      ],
    },
  ],
};

const jobTrendsData = {
  labels: ['Software Dev', 'Data Science', 'Product Management', 'UX Design', 'Marketing'],
  datasets: [
    {
      label: 'Job Postings',
      data: [65, 59, 80, 81, 56],
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
    },
    {
      label: 'Applications',
      data: [28, 48, 40, 19, 86],
      backgroundColor: 'rgba(255, 99, 132, 0.5)',
    },
  ],
};

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Chart Title',
    },
  },
};

// Dummy user data
const userData = {
  fullName: 'Kaurani Divya',
  email: 'kauranidivya@gmail.com',
  userType: 'Alumni',
  dateOfBirth: '2004-03-20',
  address: 'Laxmi Sky City',
  city: 'Ahmedabad',
  country: 'India',
  bio: 'Passionate software engineer with 3+ years of experience in web development. Committed to leveraging technology for positive social impact.',
  skills: ['JavaScript', 'React', 'Node.js', 'Gemini', 'MongoDB', 'AWS'],
  graduationYear: 2023,
  major: 'Information Technology',
  currentJob: 'Senior Software Engineer',
  company: 'York IE',
  phoneNumber: '+91 1234567890',
  linkedIn: 'https://www.linkedin.com/in/divyakaurani/',
  twitter: 'https://x.com/d_kaurani61801',
  github: 'https://github.com/KauraniDivya',
  instagram: 'https://www.instagram.com/divya_kaurani20/',
  industry: 'Information Technology',
  website: 'https://divyakaurani.vercel.app/',
  institute: 'LDRP Institute of Technology and Research',
  instituteDuration: '2019 - 2023',
  profilePhoto: 'http://res.cloudinary.com/dkyrtfk1u/image/upload/v1697563140/ablotx3mpzcysvrofysu.jpg',
  achievements: [
    'SIH 2023 Finalist',
    'SSIP Finalist',
    'BTB Winner'
  ],
  publications: [
    'Machine Learning Approaches in Cybersecurity (2018)',
    'The Future of Web Development (2020)'
  ]
};

// Dummy data for various sections
const dummyJobs = [
  { id: 1, title: 'Senior Software Engineer', company: 'Tech Corp', location: 'San Francisco, CA', salary: '$120k - $150k' },
  { id: 2, title: 'Data Scientist', company: 'Data Insights LLC', location: 'New York, NY', salary: '$100k - $130k' },
  { id: 3, title: 'Product Manager', company: 'InnovateTech', location: 'Austin, TX', salary: '$110k - $140k' },
];

const dummyEvents = [
  { id: 1, title: 'Annual Alumni Meetup', date: '2024-10-15', location: 'University Campus' },
  { id: 2, title: 'Tech Career Fair', date: '2024-11-20', location: 'Convention Center' },
  { id: 3, title: 'Entrepreneurship Workshop', date: '2024-12-05', location: 'Business School Auditorium' },
];

const dummyCampaigns = [
  { id: 1, title: 'Scholarship Fund', goal: 100000, raised: 75000 },
  { id: 2, title: 'Research Grant Program', goal: 500000, raised: 350000 },
  { id: 3, title: 'New Computer Lab', goal: 200000, raised: 180000 },
];

const dummyReferrals = [
  { id: 1, name: 'John Smith', position: 'Product Manager', company: 'InnoTech', status: 'Pending' },
  { id: 2, name: 'Sarah Johnson', position: 'UX Designer', company: 'DesignHub', status: 'Approved' },
  { id: 3, name: 'Michael Lee', position: 'Data Analyst', company: 'DataCorp', status: 'Pending' },
];

const dummyProjects = [
  { id: 1, title: 'AI-Powered Chatbot', description: 'Developed a customer service chatbot using NLP', technologies: ['Python', 'TensorFlow', 'NLP'] },
  { id: 2, title: 'E-commerce Platform', description: 'Built a scalable e-commerce solution', technologies: ['React', 'Node.js', 'MongoDB'] },
  { id: 3, title: 'Mobile Fitness App', description: 'Created a fitness tracking app for iOS and Android', technologies: ['React Native', 'Firebase'] },
];

const dummyNews = [
  { id: 1, title: 'University Ranks Top 10 in Computer Science', date: '2024-07-01' },
  { id: 2, title: 'Alumni-Founded Startup Raises $10M in Funding', date: '2024-06-15' },
  { id: 3, title: 'New Research Center Opens on Campus', date: '2024-05-30' },
];

const DashboardCard = ({ title, icon: Icon, children, className = '' }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`bg-white p-6 rounded-lg shadow-md ${className}`}
  >
    <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-800">
      <Icon className="mr-2 text-blue-500" />
      {title}
    </h2>
    {children}
  </motion.div>
);

const CreateEventForm = ({ onClose, onSubmit }) => {
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    category: '',
    description: '',
    deadline: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(eventData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Create New Event</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Event Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={eventData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event title"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={eventData.date}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="time">
                Time
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={eventData.time}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={eventData.location}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter event location"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={eventData.category}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Category</option>
              <option value="Networking">Networking</option>
              <option value="Educational">Educational</option>
              <option value="Career">Career</option>
              <option value="Social">Social</option>
              <option value="Workshop">Workshop</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={eventData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter event description"
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deadline">
              Registration Deadline
            </label>
            <input
              type="date"
              id="deadline"
              name="deadline"
              value={eventData.deadline}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Event
        </button>
      </div>
    </motion.div>
  );
};

const CreateJobForm = ({ onClose, onSubmit }) => {
  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    location: '',
    salary: '',
    type: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(jobData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Create New Job</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Job Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={jobData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter job title"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="company">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={jobData.company}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={jobData.location}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter job location"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="salary">
              Salary
            </label>
            <input
              type="text"
              id="salary"
              name="salary"
              value={jobData.salary}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter salary range"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="type">
              Job Type
            </label>
            <select
              id="type"
              name="type"
              value={jobData.type}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Job Type</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Job Description
            </label>
            <textarea
              id="description"
              name="description"
              value={jobData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter job description"
            ></textarea>
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Job
        </button>
      </div>
    </motion.div>
  );
};

const CreateCampaignForm = ({ onClose, onSubmit }) => {
  const [campaignData, setCampaignData] = useState({
    title: '',
    description: '',
    goalAmount: '',
    endDate: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCampaignData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(campaignData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Create New Campaign</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Campaign Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={campaignData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter campaign title"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={campaignData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter campaign description"
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="goalAmount">
              Goal Amount ($)
            </label>
            <input
              type="number"
              id="goalAmount"
              name="goalAmount"
              value={campaignData.goalAmount}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter goal amount"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={campaignData.endDate}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Campaign
        </button>
      </div>
    </motion.div>
  );
};

const DashboardPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);

  useEffect(() => {
    // Simulating an async operation to fetch user data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleCreateEvent = (eventData) => {
    console.log('New event created:', eventData);
    setCreateEventOpen(false);
  };

  const handleCreateJob = (jobData) => {
    console.log('New job created:', jobData);
    setCreateJobOpen(false);
  };

  const handleCreateCampaign = (campaignData) => {
    console.log('New campaign created:', campaignData);
    setCreateCampaignOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-blue-600"
        >
          Loading...
        </motion.div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <DashboardCard title="Profile Overview" icon={FaUser} className="col-span-1 md:col-span-2 lg:col-span-1">
        <div className="flex items-center mb-4">
          <img src={userData.profilePhoto} alt="Profile" className="w-20 h-20 rounded-full mr-4" />
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{userData.fullName}</h3>
            <p className="text-gray-600">{userData.currentJob} at {userData.company}</p>
          </div>
        </div>
        <div className="space-y-2 text-sm text-gray-600">
          <p><FaMapMarkerAlt className="inline mr-2 text-blue-500" />{userData.city}, {userData.country}</p>
          <p><FaPhone className="inline mr-2 text-blue-500" />{userData.phoneNumber}</p>
          <p><FaEnvelope className="inline mr-2 text-blue-500" />{userData.email}</p>
          <p><FaUniversity className="inline mr-2 text-blue-500" />{userData.institute} ({userData.instituteDuration})</p>
          <p><FaGraduationCap className="inline mr-2 text-blue-500" />{userData.major}, Class of {userData.graduationYear}</p>
        </div>
        <div className="mt-4 flex space-x-2">
          {[
            { icon: FaLinkedin, color: 'text-blue-700', url: userData.linkedIn },
            { icon: FaTwitter, color: 'text-blue-400', url: userData.twitter },
            { icon: FaGithub, color: 'text-gray-800', url: userData.github },
            { icon: FaInstagram, color: 'text-pink-600', url: userData.instagram },
            { icon: FaGlobe, color: 'text-green-600', url: userData.website },
          ].map((social, index) => (
            <a key={index} href={social.url} target="_blank" rel="noopener noreferrer" className={`${social.color} hover:opacity-80`}>
              <social.icon className="text-xl" />
            </a>
          ))}
        </div>
        <motion.button
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
  onClick={() => window.open('https://drive.google.com/file/d/1fwCf30EciDvHFFCBuoQxY5x8j_i9dxbw/view?usp=sharing', '_blank')} // Add this line
>
  <FaFileAlt className="inline mr-2" /> View Full Resume
</motion.button>

      </DashboardCard>

      <DashboardCard title="Network Growth" icon={FaChartLine}>
        <div className="h-64">
          <Line data={networkGrowthData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Your Network Growth'}}}} />
        </div>
      </DashboardCard>

      <DashboardCard title="Skills Overview" icon={FaCode}>
        <div className="h-64">
          <Radar data={skillsData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Your Skills'}}}} />
        </div>
      </DashboardCard>

      <DashboardCard title="Quick Actions" icon={FaNetworkWired}>
        <div className="grid grid-cols-2 gap-4">
          {[
            { title: 'Post Job', icon: FaBriefcase, color: 'bg-green-500', action: () => setCreateJobOpen(true) },
            { title: 'Create Event', icon: FaCalendarAlt, color: 'bg-blue-500', action: () => setCreateEventOpen(true) },
            { title: 'Mentorship', icon: FaGraduationCap, color: 'bg-purple-500' },
            { title: 'Alumni Directory', icon: FaSearch, color: 'bg-yellow-500' },
            { title: 'Donate', icon: FaHandHoldingHeart, color: 'bg-red-500' },
            { title: 'Update Profile', icon: FaUser, color: 'bg-indigo-500' },
          ].map((action, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={action.action}
              className={`${action.color} text-white px-4 py-2 rounded-lg flex items-center justify-center`}
            >
              <action.icon className="mr-2" /> {action.title}
            </motion.button>
          ))}
        </div>
      </DashboardCard>

      <DashboardCard title="Recent Achievements" icon={FaTrophy}>
        <ul className="space-y-2">
          {userData.achievements.map((achievement, index) => (
            <li key={index} className="flex items-center text-gray-700">
              <FaTrophy className="text-yellow-500 mr-2" />
              {achievement}
            </li>
          ))}
        </ul>
      </DashboardCard>

      <DashboardCard title="Publications" icon={FaBook}>
        <ul className="space-y-2">
          {userData.publications.map((publication, index) => (
            <li key={index} className="flex items-center text-gray-700">
              <FaBook className="text-blue-500 mr-2" />
              {publication}
            </li>
          ))}
        </ul>
      </DashboardCard>
    </div>
  );

  const renderConnectionsTab = () => <ConnectionsTab />;


  const renderJobsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <DashboardCard title="Job Board" icon={FaBriefcase}>
        <ul className="space-y-4">
          {dummyJobs.map(job => (
            <li key={job.id} className="border-b pb-4 last:border-b-0">
              <h3 className="font-semibold text-lg text-gray-800">{job.title}</h3>
              <p className="text-gray-600">{job.company}</p>
              <p className="text-gray-600"><FaMapMarkerAlt className="inline mr-1" />{job.location}</p>
              <p className="text-gray-600"><FaDollarSign className="inline mr-1" />{job.salary}</p>
            </li>
          ))}
        </ul>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCreateJobOpen(true)}
          className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
        >
          <FaPlus className="inline mr-2" /> Post a Job
        </motion.button>
      </DashboardCard>

      <DashboardCard title="Job Market Trends" icon={FaChartBar}>
        <div className="h-80">
          <Bar data={jobTrendsData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Job Market Trends'}}}} />
        </div>
      </DashboardCard>
    </div>
  );

  const renderEventsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <DashboardCard title="Upcoming Events" icon={FaCalendarAlt}>
        <ul className="space-y-4">
          {dummyEvents.map(event => (
            <li key={event.id} className="border-b pb-4 last:border-b-0">
              <h3 className="font-semibold text-lg text-gray-800">{event.title}</h3>
              <p className="text-gray-600"><FaCalendarAlt className="inline mr-1" />{event.date}</p>
              <p className="text-gray-600"><FaMapMarkerAlt className="inline mr-1" />{event.location}</p>
            </li>
          ))}
        </ul>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCreateEventOpen(true)}
          className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
        >
          <FaPlus className="inline mr-2" /> Create Event
        </motion.button>
      </DashboardCard>

      <DashboardCard title="Event Calendar" icon={FaCalendarAlt}>
        {/* Here you would typically integrate a calendar component */}
        <CalendarComponent />
      </DashboardCard>
    </div>
  );

  const renderCampaignsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <DashboardCard title="Active Campaigns" icon={FaHandHoldingHeart}>
        <ul className="space-y-4">
          {dummyCampaigns.map(campaign => (
            <li key={campaign.id} className="border-b pb-4 last:border-b-0">
              <h3 className="font-semibold text-lg text-gray-800">{campaign.title}</h3>
              <p className="text-gray-600">Goal: ${campaign.goal.toLocaleString()}</p>
              <div className="mt-2 bg-gray-200 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-green-500 h-full rounded-full" 
                  style={{width: `${(campaign.raised / campaign.goal) * 100}%`}}
                ></div>
              </div>
              <p className="text-gray-600 mt-1">Raised: ${campaign.raised.toLocaleString()} ({((campaign.raised / campaign.goal) * 100).toFixed(1)}%)</p>
            </li>
          ))}
        </ul>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCreateCampaignOpen(true)}
          className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
        >
          <FaPlus className="inline mr-2" /> Create Campaign
        </motion.button>
      </DashboardCard>

      <DashboardCard title="Donation Overview" icon={FaChartPie}>
        <div className="h-80">
          <Doughnut data={donationData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Donation Breakdown'}}}} />
        </div>
      </DashboardCard>
    </div>
  );

  const renderProjectsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {dummyProjects.map(project => (
        <DashboardCard key={project.id} title={project.title} icon={FaProjectDiagram}>
          <p className="text-gray-600 mb-2">{project.description}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {project.technologies.map((tech, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {tech}
              </span>
            ))}
          </div>
        </DashboardCard>
      ))}
    </div>
  );

  const renderReferralsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <DashboardCard title="Referral Requests" icon={FaUserFriends}>
        <ul className="space-y-4">
          {dummyReferrals.map(referral => (
            <li key={referral.id} className="border-b pb-4 last:border-b-0">
              <h3 className="font-semibold text-lg text-gray-800">{referral.name}</h3>
              <p className="text-gray-600">{referral.position} at {referral.company}</p>
              <p className={`text-sm ${referral.status === 'Approved' ? 'text-green-600' : 'text-yellow-600'}`}>
                Status: {referral.status}
              </p>
            </li>
          ))}
        </ul>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
        >
          <FaPlus className="inline mr-2" /> Send Referral
        </motion.button>
      </DashboardCard>

      <DashboardCard title="Referral Statistics" icon={FaChartBar}>
        {/* Here you would typically add a chart showing referral statistics */}
        <ReferralStatisticsChart />

      
      </DashboardCard>
    </div>
  );

  const renderNewsTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {dummyNews.map(news => (
        <DashboardCard key={news.id} title={news.title} icon={FaNewspaper}>
          <p className="text-gray-600 mb-2">{news.date}</p>
          <p className="text-gray-700">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition duration-300"
          >
            Read More
          </motion.button>
        </DashboardCard>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">Welcome, {userData.fullName}!</h1>
          <div className="flex space-x-4">
            <motion.button whileHover={{ scale: 1.1 }} className="text-gray-600 hover:text-gray-800">
              <FaBell className="text-2xl" />
            </motion.button>
            <motion.button whileHover={{ scale: 1.1 }} className="text-gray-600 hover:text-gray-800">
              <FaEnvelope className="text-2xl" />
            </motion.button>
            <motion.button whileHover={{ scale: 1.1 }} className="text-gray-600 hover:text-gray-800">
              <FaCog className="text-2xl" />
            </motion.button>
          </div>
        </div>

        <div className="mb-8">
          <nav className="flex flex-wrap justify-center md:justify-start space-x-4">
            {[
              { id: 'overview', label: 'Overview', icon: FaUser },
              { id: 'connections', label: 'Connections', icon: FaUserFriends }, 
              { id: 'jobs', label: 'Jobs', icon: FaBriefcase },
              { id: 'events', label: 'Events', icon: FaCalendarAlt },
              { id: 'campaigns', label: 'Campaigns', icon: FaHandHoldingHeart },
              { id: 'projects', label: 'Projects', icon: FaProjectDiagram },
              { id: 'referrals', label: 'Referrals', icon: FaUserFriends },
              { id: 'news', label: 'News', icon: FaNewspaper },
            ].map(tab => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <tab.icon className="mr-2" />
                {tab.label}
              </motion.button>
            ))}
          </nav>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'connections' && renderConnectionsTab()}
            {activeTab === 'jobs' && renderJobsTab()}
            {activeTab === 'events' && renderEventsTab()}
            {activeTab === 'campaigns' && renderCampaignsTab()}
            {activeTab === 'projects' && renderProjectsTab()}
            {activeTab === 'referrals' && <ReferralsTab />}
            {activeTab === 'news' && renderNewsTab()}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>
          {createEventOpen && (
            <CreateEventForm onClose={() => setCreateEventOpen(false)} onSubmit={handleCreateEvent} />
          )}
          {createJobOpen && (
            <CreateJobForm onClose={() => setCreateJobOpen(false)} onSubmit={handleCreateJob} />
          )}
          {createCampaignOpen && (
            <CreateCampaignForm onClose={() => setCreateCampaignOpen(false)} onSubmit={handleCreateCampaign} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DashboardPage;